'use strict';

import trim from 'lodash/trim';

//IOTAYS-181 allow numbers, whitespace, - and +358
export const phoneRegex = /^(\+358|0)[0-9]{2}[\s-]?[\s\-0-9]{5,15}$/;
export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const postalCodeRegex = /^(|[0-9]{5})$/;
export const phoneLengthRegex = /^(\+358)/;
export const dashRegex = /([0-9]{3}-)/;

// TODO: Add phoneLengthRegex checkup to ALL of the validations. Missing 1 atleast

function isEmpty(value) {
  return value === null || value === undefined || trim(value) === '';
}

function isEqual(value1, value2) {
  return value1 === value2;
}

function isValidPhone(value) {
  return phoneRegex.test(value);
}

function isValidEmail(value) {
  return emailRegex.test(value);
}

/**
 *
 * Validation errors are given as array with the key related to validation, as follows:
 *
 * {
 *  phone: [
 *    "phone.is.empty"
 *  ],
 *  confrimPhone: [
 *    "phones.not.matching"
 *  ]
 * }
 *
 * */
class Validation {
  /**
   * Validate email when input is asked twice and must match
   *
   * @param {String} email The email to be validated
   * @param {String} confirmEmail The confirmation of email, this must match the first "email" argument.
   * @param {Object} errors Existing errors object to populate with errors (optional)
   * @param {Boolean} extended indicates if empty field should include extended .empty message. Used in Omat tiedot.
   * @returns {Object} Errors object with errors defined by keys
   * */
  static validateEmail = (email, confirmEmail, errors = {}) => {
    errors.email = [];
    errors.confirmEmail = [];

    if (!isEqual(email, confirmEmail)) {
      errors.confirmEmail.push('emails.not.matching');
    }

    if (isEmpty(email)) {
      errors.email.push('email.is.empty');
    }

    if (isEmpty(confirmEmail)) {
      errors.confirmEmail.push('email.is.empty');
    }

    if (email && !isValidEmail(email)) {
      errors.email.push('email.is.malformed');
    }

    if (confirmEmail && !isValidEmail(confirmEmail)) {
      errors.confirmEmail.push('email.is.malformed');
    }

    return errors;
  };
  /**
   * Validate email when inputs in agreement/registration must match
   *
   * @param {String} email The email to be validated
   * @param {String} confirmEmail The confirmation of email, this must match the first "email" argument.
   * @param {Object} errors Existing errors object to populate with errors (optional)
   * @returns {Object} Errors object with errors defined by keys
   * */
  static validateEmailInAgreement = (email, confirmEmail, errors = {}) => {
    errors.email = [];
    errors.confirmEmail = [];

    if (!isEqual(email, confirmEmail)) {
      errors.confirmEmail.push('emails.not.matching');
    }

    if (isEmpty(email)) {
      errors.email.push('email.is.empty');
    }

    if (isEmpty(confirmEmail)) {
      errors.confirmEmail.push('email.is.empty');
    }

    if (email && !isValidEmail(email)) {
      errors.email.push('email.is.malformed');
    }

    if (confirmEmail && !isValidEmail(confirmEmail)) {
      errors.confirmEmail.push('email.is.malformed');
    }

    return errors;
  };

  /**
   * Validate email when input is asked twice and must match, but is not required
   *
   * @param {String} email The phone number to be validated
   * @param {String} confirmEmail The confirmation of phone number, this must match the first "phone" argument.
   * @param {Object} errors Existing errors object to populate with errors (optional)
   * @returns {Object} Errors object with errors defined by keys
   * */
  static validateEmailNoRequirement = (email, confirmEmail, errors = {}) => {
    if (!isEqual(email, confirmEmail)) {
      (errors.confirmEmail
        ? errors.confirmEmail
        : (errors.confirmEmail = [])
      ).push('emails.not.matching');
    }

    if (email && !isValidEmail(email)) {
      (errors.email ? errors.email : (errors.email = [])).push(
        'email.is.malformed'
      );
    }

    return errors;
  };

  /**
   * Validate phone number when input is asked twice and must match
   *
   * @param {String} fieldName Name of the primary field (not confirm field)
   * @param {String} phone The phone number to be validated
   * @param {String} confirmPhone The confirmation of phone number, this must match the first "phone" argument.
   * @param {Object} errors Existing errors object to populate with errors (optional)
   * @returns {Object} Errors object with errors defined by keys
   * */
  static validatePhone = (fieldName, phone, confirmPhone, errors = {}) => {
    errors[fieldName] = [];
    errors[`${fieldName}Confirm`] = [];

    if (!isEqual(phone?.trim(), confirmPhone?.trim())) {
      errors[`${fieldName}Confirm`].push('phones.not.matching');
    }

    if (isEmpty(phone)) {
      errors[fieldName].push('phone.is.empty');
    } else {
      const trimmedPhone = phone.trim();

      if (!isValidPhone(trimmedPhone)) {
        errors[fieldName].push('phone.is.malformed');
      }
    }

    if (isEmpty(confirmPhone)) {
      errors[`${fieldName}Confirm`].push('phone.is.empty');
    } else {
      const trimmedConfirmPhone = confirmPhone.trim();

      if (!isValidPhone(trimmedConfirmPhone)) {
        errors[`${fieldName}Confirm`].push('phone.is.malformed');
      }
    }

    return errors;
  };

  /**
   * Validate phone number for agreement
   *
   * @param {String} phone The phone number to be validated
   * @param {String} confirmPhone The confirmation of phone number, this must match the first "phone" argument.
   * @param {Object} errors Existing errors object to populate with errors (optional)
   * @returns {Object} Errors object with errors defined by keys
   * */
  static validatePhoneInAgreement = (phone, confirmPhone, errors = {}) => {
    if (!isEqual(phone, confirmPhone)) {
      (errors.confirmPhone
        ? errors.confirmPhone
        : (errors.confirmPhone = [])
      ).push('phones.not.matching');
    }

    if (phone && !isValidPhone(phone)) {
      (errors.phone ? errors.phone : (errors.phone = [])).push(
        'phone.is.malformed'
      );
    }

    if ((!isEmpty(phone) && phone.length < 7) || phone.length > 13) {
      (errors.phone ? errors.phone : (errors.phone = [])).push(
        'phone.length.error'
      );
    }

    return errors;
  };

  /**
   *
   * Validate phone number when input is asked once (no confirmation double checking)
   *
   * @param {String} phone The phone number to be validated
   * @param {Object} errors Existing errors object to populate with errors (optional)
   * @returns {Object} Errors object with errors defined by keys
   * */
  static validatePhoneWithoutConfirm = (phone, errors = {}) => {
    if (isEmpty(phone)) {
      (errors.phone ? errors.phone : (errors.phone = [])).push(
        'phone.is.empty'
      );
    }

    if (phone && !isValidPhone(phone)) {
      (errors.phone ? errors.phone : (errors.phone = [])).push(
        'phone.is.malformed'
      );
    }

    return errors;
  };
}

export default Validation;
